import * as TelemetryContext from "../context"

const GA_DATA_LAYER_NAME = "liqaDataLayer"
const GA_ID = import.meta.env.VITE_GA_MEASUREMENT_ID

export const event = (...args: Parameters<GTag>) => gtag("event", ...args)

let gtag: GTag = (...args) => {
  // Adopted default Google Analytics code from Google Analytics console

  // WORKAROUND: Google Analytics relies on `location` which is `about:blank` inside the Realm.
  // Monkey-patching `gtag.js` code, supplying it `location` from the Realm‘s parent.
  fetch(`https://www.googletagmanager.com/gtag/js?id=${GA_ID}&l=${GA_DATA_LAYER_NAME}`)
    .then((response) => {
      /* Handle browser's / VPN‘s built-in tracking blockers */
      if (!response.ok)
        throw new Error(
          `Failed to load ${response.url} ${response.status} (${response.statusText})`,
        )

      return response
    })
    .catch(() => {
      /* Usually, the failure is caused by the strict CSP rules */

      // Retry to get the Analytics script from LIQA proxy server
      return fetch(new URL(`/gtag/js?id=${GA_ID}&l=${GA_DATA_LAYER_NAME}`, import.meta["url"]))
    })
    .then((response) => {
      if (!response.ok)
        throw new Error(
          `Failed to load ${response.url} ${response.status} (${response.statusText})`,
        )
      return response.text()
    })
    .then((src) =>
      // override location referenced in the gtag.js with a proxy to parent's location
      // to avoid using the Realm‘s location which is `about:blank`
      src.replace(
        "(function(){",
        (match) =>
          match +
          /*
           * Intentionally pre-minified code to reduce the size of the final bundle.
           * Minifier: https://www.toptal.com/developers/javascript-minifier
           * Original code:
           * ```javascript
           *  var document = new Proxy(self.document, {
           *    get: (target, property) => {
           *      if (property === "location")
           *        return (parent||self)[property]
           *
           *      const value = target[property]
           *      if (typeof value === "function")
           *        return new Proxy(value, {
           *          apply: (method, thisArg, argumentsList) =>
           *            method.apply(target, argumentsList)
           *        })
           *
           *      return value
           *    }
           *  })
           * ```
           */
          /* javascript */ `var document=new Proxy(self.document,{get(t,p){if("location"===p)return(parent||self)[p];let v=t[p];return"function"==typeof v?new Proxy(v,{apply:(m,_,a)=>m.apply(t,a)}):v}});`,
      ),
    )
    .then((src) => {
      const script = document.createElement("script")
      const content = document.createTextNode(src)
      script.appendChild(content), document.head.appendChild(script)
    })

  self[GA_DATA_LAYER_NAME] = self[GA_DATA_LAYER_NAME] || []

  gtag = function () {
    // Google Analytics requires to use `arguments` instead of `...args` rest parameter to work properly.
    // If anything but `arguments` is used, the events won't be sent to Google Analytics.
    self[GA_DATA_LAYER_NAME].push(arguments)
  }

  gtag("js", new Date())
  gtag("config", GA_ID, {
    send_page_view: false,
    transport_type: "beacon",
    // We would like to use gtag("set", { release: import.meta.env.VERSION })
    // But it does not work https://stackoverflow.com/a/75618823
    release: import.meta.env.VERSION,
    license: TelemetryContext.get()?.tags?.license,
  })

  gtag(...args)
}

type GTag = (eventName: string, ...args: any[]) => void

declare global {
  interface Window {
    [GA_DATA_LAYER_NAME]: any[]
  }
}
